// AdditionalInformations.tsx
import React, { useMemo, useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react'
import { Box } from '@mui/material'
import { EmtChip, EmtSelect, EmtTypography } from 'emt-components-v2'

export type ServiceSlug = 'promenade' | 'visite' | 'garde-chez-eux' | 'garde-chez-vous'

type ChipItem = { field: string; label: string; scheduled?: boolean }
export type AdditionalInformationsValues = {
  walkBehaviors?: string[]
  indoorCleanliness?: 'never' | 'rare' | 'often'
  indoorBehaviors?: string[]
  timeAlone?: string
  housing?: string
  summary?: string
}

interface Props {
  service: string
  hasSelectedDog: boolean
  onChange?: (values: Partial<AdditionalInformationsValues>) => void
  values: AdditionalInformationsValues
  setValues: React.Dispatch<React.SetStateAction<AdditionalInformationsValues>>
}

export type AdditionalInformationsHandle = {
  validate: () => boolean
  scrollToFirstError?: () => void
}

type Errors = {
  walkBehaviors?: string
  indoorCleanliness?: string
  indoorBehaviors?: string
  timeAlone?: string
  housing?: string
}

const WALK_BEHAVIORS: ChipItem[] = [
  { field: 'energetic', label: 'Energique' },
  { field: 'calm', label: 'Calme' },
  { field: 'player', label: 'Joueur' },
  { field: 'pulls', label: 'Tire en laisse' },
  { field: 'reactive_dogs', label: 'Réactif aux chiens' },
  { field: 'reactive_humans', label: 'Réactif aux humains' },
  { field: 'reactive_vehicles', label: 'Réactif aux trottinettes / vélos' },
  { field: 'non-applicable', label: 'Non applicable' },
]

const INDOOR_BEHAVIORS: ChipItem[] = [
  { field: 'calm', label: 'Calme' },
  { field: 'energetic', label: 'Energique' },
  { field: 'player', label: 'Joueur' },
  { field: 'thief', label: 'Voleur' },
  { field: 'cuddly', label: 'Câlin' },
  { field: 'shy', label: 'Timide' },
  { field: 'fearful', label: 'Peureux' },
]

const CLEANLINESS: { field: AdditionalInformationsValues['indoorCleanliness']; label: string }[] = [
  { field: 'never', label: "Jamais d'accident" },
  { field: 'rare', label: 'Accidents très rares' },
  { field: 'often', label: 'Accidents réguliers' },
]

const TIME_ALONE_OPTIONS = [
  { value: '1h', label: '1 heure' },
  { value: '2h', label: '2 heures' },
  { value: '3h', label: '3 heures' },
  { value: '5h', label: '5 heures' },
  { value: '8h', label: '8 heures' },
  { value: 'gt8', label: 'Plus de 8 heures' },
]

const HOUSING_OPTIONS = [
  { value: 'apt_lt20', label: 'Appartement moins de 20 m2' },
  { value: 'apt_20_40', label: 'Appartement 20 - 40 m2' },
  { value: 'apt_gt40', label: 'Appartement + 40 m2' },
  { value: 'house_no_garden', label: 'Maison sans jardin' },
  { value: 'house_garden', label: 'Maison avec jardin' },
]

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <EmtTypography variant="body1" fontWeight={700} sx={{ mb: '12px' }}>
    {children}
  </EmtTypography>
)

const ChipGrid: React.FC<{
  chips: ChipItem[]
  onClick: (field: string) => void
}> = ({ chips, onClick }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
    }}
  >
    {chips.map((chip, idx) => (
      <EmtChip
        key={idx}
        sx={{
          border: `1px solid ${chip.scheduled ? '#19BA92' : '#D2D1D4'}`,
          color: chip.scheduled ? '#18BA92!important' : '#77757F!important',
          height: '40px',
          borderRadius: '100vmax',
          cursor: 'pointer',
          backgroundColor: chip.scheduled ? '#E9FDF8' : '#F9F9F9',
          '&:hover': {
            backgroundColor: chip.scheduled ? '#E9FDF8' : '#f0f0f0',
          },
        }}
        onClick={() => onClick(chip.field)}
        color={chip.scheduled ? 'successlight' : 'default'}
        label={chip.label}
      />
    ))}
  </Box>
)

const labelFrom = (value: string, items: { value: string; label: string }[]) =>
  items.find((i) => i.value === value)?.label ?? value

const buildSummary = (values: AdditionalInformationsValues, service: ServiceSlug, hasSelectedDog: boolean): string => {
  const lines: string[] = []

  // Promenade
  if (
    (service === 'promenade' || (hasSelectedDog && (service === 'garde-chez-vous' || service === 'garde-chez-eux'))) &&
    values.walkBehaviors?.length
  ) {
    const labels = values.walkBehaviors
      .map((code) => WALK_BEHAVIORS.find((c) => c.field === code)?.label ?? code)
      .join(', ')
    lines.push(`**Comportement en promenade :** ${labels}`)
  }

  // Propreté + comportement intérieur
  if (service !== 'promenade') {
    if (values.indoorCleanliness) {
      const label = CLEANLINESS.find((c) => c.field === values.indoorCleanliness)?.label ?? values.indoorCleanliness
      lines.push(`**Propreté à l’intérieur :** ${label}`)
    }
    if (values.indoorBehaviors?.length) {
      const labels = values.indoorBehaviors
        .map((code) => INDOOR_BEHAVIORS.find((c) => c.field === code)?.label ?? code)
        .join(', ')
      lines.push(`**Comportement à l’intérieur :** ${labels}`)
    }
  }

  // Temps seul
  if ((service === 'garde-chez-vous' || service === 'garde-chez-eux') && values.timeAlone) {
    const label = labelFrom(values.timeAlone, TIME_ALONE_OPTIONS)
    lines.push(`**Temps maximal pendant lequel le ou les animaux peuvent rester seul(s) :** ${label}`)
  }

  // Logement
  if (service === 'garde-chez-vous' && values.housing) {
    const label = labelFrom(values.housing, HOUSING_OPTIONS)
    lines.push(`**Type de logement :** ${label}`)
  }

  return lines.join('\n\n')
}

const getInitialValuesForService = (
  service: ServiceSlug,
  hasSelectedDog: boolean,
  prevValues: AdditionalInformationsValues,
): AdditionalInformationsValues => {
  // Always keep summary up-to-date
  let base: AdditionalInformationsValues = {}

  switch (service) {
    case 'promenade':
      base.walkBehaviors = prevValues.walkBehaviors ?? []
      break
    case 'visite':
      base.indoorCleanliness = prevValues.indoorCleanliness
      base.indoorBehaviors = prevValues.indoorBehaviors ?? []
      break
    case 'garde-chez-eux':
      base.indoorCleanliness = prevValues.indoorCleanliness
      base.indoorBehaviors = prevValues.indoorBehaviors ?? []
      base.timeAlone = prevValues.timeAlone
      if (hasSelectedDog) base.walkBehaviors = prevValues.walkBehaviors ?? []
      break
    case 'garde-chez-vous':
      base.indoorCleanliness = prevValues.indoorCleanliness
      base.indoorBehaviors = prevValues.indoorBehaviors ?? []
      base.timeAlone = prevValues.timeAlone
      base.housing = prevValues.housing
      if (hasSelectedDog) base.walkBehaviors = prevValues.walkBehaviors ?? []
      break
    default:
      break
  }
  // Always update summary
  base.summary = buildSummary(base, service, hasSelectedDog)
  return base
}

const AdditionalInformations = forwardRef<AdditionalInformationsHandle, Props>(
  ({ service, hasSelectedDog, onChange, values, setValues }, ref) => {
    const currentService = (service || '') as ServiceSlug

    const [errors, setErrors] = useState<Errors>({})

    const rootRef = useRef<HTMLDivElement | null>(null)
    const walkRef = useRef<HTMLDivElement | null>(null)
    const indoorCleanlinessRef = useRef<HTMLDivElement | null>(null)
    const indoorBehaviorsRef = useRef<HTMLDivElement | null>(null)
    const timeAloneRef = useRef<HTMLDivElement | null>(null)
    const housingRef = useRef<HTMLDivElement | null>(null)

    const walkChips = useMemo<ChipItem[]>(
      () => WALK_BEHAVIORS.map((c) => ({ ...c, scheduled: values.walkBehaviors?.includes(c.field) })),
      [values.walkBehaviors],
    )
    const indoorChips = useMemo<ChipItem[]>(
      () => INDOOR_BEHAVIORS.map((c) => ({ ...c, scheduled: values.indoorBehaviors?.includes(c.field) })),
      [values.indoorBehaviors],
    )
    const cleanlinessChips = useMemo<ChipItem[]>(
      () =>
        CLEANLINESS.map((c) => ({
          field: c.field || '',
          label: c.label,
          scheduled: values.indoorCleanliness === c.field,
        })),
      [values.indoorCleanliness],
    )

    const emit = (patch: Partial<AdditionalInformationsValues>) => {
      const next = { ...values, ...patch }
      const summary = buildSummary(next, currentService, hasSelectedDog)
      const nextWithSummary = { ...next, summary }

      setValues(nextWithSummary)
      onChange?.({ ...patch, summary })

      // on nettoie les erreurs liées aux clés modifiées
      setErrors((prev) => {
        const copy = { ...prev }
        if ('walkBehaviors' in patch) delete copy.walkBehaviors
        if ('indoorCleanliness' in patch) delete copy.indoorCleanliness
        if ('indoorBehaviors' in patch) delete copy.indoorBehaviors
        if ('timeAlone' in patch) delete copy.timeAlone
        if ('housing' in patch) delete copy.housing
        return copy
      })
    }

    const toggleWalk = (field: string) => {
      const set = new Set(values.walkBehaviors)
      set.has(field) ? set.delete(field) : set.add(field)
      emit({ walkBehaviors: Array.from(set) })
    }

    const toggleIndoor = (field: string) => {
      const set = new Set(values.indoorBehaviors)
      set.has(field) ? set.delete(field) : set.add(field)
      emit({ indoorBehaviors: Array.from(set) })
    }

    const selectCleanliness = (field: string) => {
      emit({ indoorCleanliness: field as AdditionalInformationsValues['indoorCleanliness'] })
    }

    const handleTimeAloneChange = (v: string) => {
      emit({ timeAlone: v })
    }

    const handleHousingChange = (v: string) => {
      emit({ housing: v })
    }

    // 🔍 Validation par champ selon le service
    const validate = (): boolean => {
      const nextErrors: Errors = {}
      let firstErrorElement: HTMLElement | null = null

      if (currentService === 'promenade') {
        if (!values.walkBehaviors?.length) {
          nextErrors.walkBehaviors = 'Merci de renseigner au moins un comportement en promenade.'
          if (!firstErrorElement && walkRef.current) firstErrorElement = walkRef.current
        }
      }

      if (currentService === 'visite') {
        if (!values.indoorCleanliness) {
          nextErrors.indoorCleanliness = 'Merci de préciser sa propreté à l’intérieur.'
          if (!firstErrorElement && indoorCleanlinessRef.current) firstErrorElement = indoorCleanlinessRef.current
        }
        if (!values.indoorBehaviors?.length) {
          nextErrors.indoorBehaviors = 'Merci de préciser son comportement à l’intérieur.'
          if (!firstErrorElement && indoorBehaviorsRef.current) firstErrorElement = indoorBehaviorsRef.current
        }
      }

      if (currentService === 'garde-chez-vous') {
        if (!values.indoorCleanliness) {
          nextErrors.indoorCleanliness = 'Merci de préciser sa propreté à l’intérieur.'
          if (!firstErrorElement && indoorCleanlinessRef.current) firstErrorElement = indoorCleanlinessRef.current
        }
        if (!values.indoorBehaviors?.length) {
          nextErrors.indoorBehaviors = 'Merci de préciser son comportement à l’intérieur.'
          if (!firstErrorElement && indoorBehaviorsRef.current) firstErrorElement = indoorBehaviorsRef.current
        }
        if (!values.timeAlone) {
          nextErrors.timeAlone = 'Merci de préciser combien de temps il peut rester seul.'
          if (!firstErrorElement && timeAloneRef.current) firstErrorElement = timeAloneRef.current
        }
        if (!values.housing) {
          nextErrors.housing = 'Merci de préciser le type de logement.'
          if (!firstErrorElement && housingRef.current) firstErrorElement = housingRef.current
        }
        if (!values.walkBehaviors?.length && hasSelectedDog) {
          nextErrors.walkBehaviors = 'Merci de renseigner au moins un comportement en promenade.'
          if (!firstErrorElement && walkRef.current) firstErrorElement = walkRef.current
        }
        if (!values.housing) {
          nextErrors.housing = 'Merci de préciser le type de logement.'
        }
      }

      if (currentService === 'garde-chez-eux') {
        if (!values.indoorCleanliness) {
          nextErrors.indoorCleanliness = 'Merci de préciser sa propreté à l’intérieur.'
          if (!firstErrorElement && indoorCleanlinessRef.current) firstErrorElement = indoorCleanlinessRef.current
        }
        if (!values.indoorBehaviors?.length) {
          nextErrors.indoorBehaviors = 'Merci de préciser son comportement à l’intérieur.'
          if (!firstErrorElement && indoorBehaviorsRef.current) firstErrorElement = indoorBehaviorsRef.current
        }
        if (!values.timeAlone) {
          nextErrors.timeAlone = 'Merci de préciser combien de temps il peut rester seul.'
          if (!firstErrorElement && timeAloneRef.current) firstErrorElement = timeAloneRef.current
        }
        if (!values.walkBehaviors?.length && hasSelectedDog) {
          nextErrors.walkBehaviors = 'Merci de renseigner au moins un comportement en promenade.'
          if (!firstErrorElement && walkRef.current) firstErrorElement = walkRef.current
        }
      }

      setErrors(nextErrors)
      return Object.keys(nextErrors).length === 0
    }
    const scrollToFirstError = () => {
      if (errors.walkBehaviors && walkRef.current) {
        walkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (errors.indoorCleanliness && indoorCleanlinessRef.current) {
        indoorCleanlinessRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (errors.indoorBehaviors && indoorBehaviorsRef.current) {
        indoorBehaviorsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (errors.timeAlone && timeAloneRef.current) {
        timeAloneRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (errors.housing && housingRef.current) {
        housingRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (rootRef.current) {
        rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    useImperativeHandle(ref, () => ({
      validate,
      scrollToFirstError,
    }))

    // Reset values not relevant to the current service when service changes
    useEffect(() => {
      const currentService = (service || '') as ServiceSlug
      const next = getInitialValuesForService(currentService, hasSelectedDog, values)
      emit(next)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service, hasSelectedDog])

    const renderWalkSection = () => {
      return (
        <Box ref={walkRef}>
          <SectionTitle>Comportement en promenade :</SectionTitle>
          <ChipGrid chips={walkChips} onClick={toggleWalk} />
          {errors.walkBehaviors && (
            <EmtTypography
              variant="body2"
              fontSize="0.75rem"
              color="error"
              data-form-error="true"
              sx={{ mt: '6px', letterSpacing: '0.03333em' }}
            >
              {errors.walkBehaviors}
            </EmtTypography>
          )}
        </Box>
      )
    }

    const renderIndoorSections = () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Box>
          <SectionTitle>Propreté de votre / vos animaux à l’intérieur :</SectionTitle>
          <ChipGrid chips={cleanlinessChips} onClick={selectCleanliness} />
          {errors.indoorCleanliness && (
            <EmtTypography
              variant="body2"
              fontSize="0.75rem"
              color="error"
              data-form-error="true"
              sx={{ mt: '6px', letterSpacing: '0.03333em' }}
            >
              {errors.indoorCleanliness}
            </EmtTypography>
          )}
        </Box>
        <Box>
          <SectionTitle>Comportement à l’intérieur :</SectionTitle>
          <ChipGrid chips={indoorChips} onClick={toggleIndoor} />
          {errors.indoorBehaviors && (
            <EmtTypography
              variant="body2"
              fontSize="0.75rem"
              color="error"
              data-form-error="true"
              sx={{ mt: '6px', letterSpacing: '0.03333em' }}
            >
              {errors.indoorBehaviors}
            </EmtTypography>
          )}
        </Box>
      </Box>
    )

    const renderTimeAlone = () => (
      <Box ref={timeAloneRef}>
        <SectionTitle>Combien de temps votre ou vos animaux peuvent-ils rester seuls ?</SectionTitle>
        <EmtSelect
          options={TIME_ALONE_OPTIONS}
          value={values.timeAlone || ''}
          onChange={handleTimeAloneChange}
          inputLabel="Durée maximale"
          // @ts-expect-error
          color="purple"
          sx={{ maxWidth: 320 }}
        />
        {errors.timeAlone && (
          <EmtTypography
            variant="body2"
            fontSize="0.75rem"
            color="error"
            data-form-error="true"
            sx={{ mt: '6px', letterSpacing: '0.03333em' }}
          >
            {errors.timeAlone}
          </EmtTypography>
        )}
      </Box>
    )

    const renderHousing = () => (
      <Box ref={housingRef}>
        <SectionTitle>Votre logement</SectionTitle>
        <EmtSelect
          options={HOUSING_OPTIONS}
          value={values.housing || ''}
          onChange={handleHousingChange}
          inputLabel="Votre logement"
          // @ts-expect-error
          color="purple"
          sx={{ maxWidth: 420 }}
        />
        {errors.housing && (
          <EmtTypography
            variant="body2"
            fontSize="0.75rem"
            color="error"
            data-form-error="true"
            sx={{ mt: '6px', letterSpacing: '0.03333em' }}
          >
            {errors.housing}
          </EmtTypography>
        )}
      </Box>
    )

    if (currentService === 'promenade') {
      return <Box ref={rootRef}>{renderWalkSection()}</Box>
    }

    if (currentService === 'visite') {
      return <Box ref={rootRef}>{renderIndoorSections()}</Box>
    }

    if (currentService === 'garde-chez-eux') {
      return (
        <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          {renderIndoorSections()}
          {renderTimeAlone()}
          {renderWalkSection()}
        </Box>
      )
    }

    if (currentService === 'garde-chez-vous') {
      return (
        <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          {renderIndoorSections()}
          {renderTimeAlone()}
          {renderWalkSection()}
          {renderHousing()}
        </Box>
      )
    }

    return <Box ref={rootRef} />
  },
)

AdditionalInformations.displayName = 'AdditionalInformations'
export default AdditionalInformations
